package ru.it_mozaika.sender.common.models

import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.Contextual
import kotlinx.serialization.Serializable


@Serializable
data class EmailTemplateData(
    val id: String = "",
    val store: String = "",
    val name: String = "",
    val template: EmailTemplate = EmailTemplate.BlockEditor(),
    @Contextual val updateTime: Instant = Clock.System.now(),
    val favicon: String = "",
    val type: TemplateType
)
@Serializable
data class EmailTemplateCreateRequest(
    val template: EmailTemplate,
    val type: String,
    val id: String? = null,
    val name: String? = null,
    val store: String? = null,
    val favicon: String = "",
)

@Serializable
data class EmailTemplateWithName(
    val name: String,
    val template: EmailTemplate,
)

@Serializable
sealed class EmailTemplate {
    @Serializable
    data class Freemarker(
        var template: String = "<#compress>\n<!DOCTYPE html PUBLIC \"-//W3C//DTD XHTML 1.0 Transitional//EN\" \"http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd\">\n\n<html xmlns=\"http://www.w3.org/1999/xhtml\" lang=\"en\" xml:lang=\"en\" style=\"background:#fff!important\">\n<head>\n</head>\n<body>\n</body>\n</html>\n</#compress>",
        var content: List<EmailElement> = listOf(),
        var title: String = "",
    ) : EmailTemplate()

    @Serializable
    data class BlockEditor(
        var settings: EmailTemplatePageSettings = EmailTemplatePageSettings(),
        var rows: List<EmailTemplateRow> = listOf(EmailTemplateRow()),
    ) : EmailTemplate()
}
@Serializable
sealed class EmailElement {
    abstract var elementName: String

    @Serializable
    data class Banner(var href: String = "", var imageUrl: String = "", var alt: String = "", override var elementName: String = "") : EmailElement()
    @Serializable
    data class Button(var href: String = "", var title: String = "", override var elementName: String = "") : EmailElement()
    @Serializable
    data class Separator(var title: String = "", override var elementName: String = "") : EmailElement()
    @Serializable
    data class Product(var id: String = "", var title: String = "")
    @Serializable
    data class ProductsBlock(var products: MutableList<Product> = mutableListOf(), override var elementName: String = "") : EmailElement()
    @Serializable
    data class BasketBlock(override var elementName: String = "") : EmailElement()
    @Serializable
    data class ResultBlock(override var elementName: String = "") : EmailElement()
    @Serializable
    data class NewsBlock(var news: MutableList<News> = mutableListOf(), override var elementName: String = "") : EmailElement()
    @Serializable
    data class News(var href: String = "", var imageUrl: String = "", var title: String = "", var description: String = "")
    @Serializable
    data class BannersBlock(var banners: MutableList<Banner> = mutableListOf(), override var elementName: String = "") : EmailElement()
}


@Serializable
sealed class DataSource {
    @Serializable
    data class ProductsCategory(val id: String) : DataSource() {
        @Serializable
        enum class Field {
            Id, Name
        }
    }
    @Serializable
    data class Product(val id: String) : DataSource() {
        @Serializable
        enum class Field {
            Id, Name, Available, Url, Picture, Price, Category, Description, Vendor, VendorCode
        }
    }
    @Serializable
    object Store : DataSource() {
        @Serializable
        enum class Field {
            Id, Name, Url
        }
    }
    @Serializable
    object Subscriber : DataSource() {
        @Serializable
        enum class Field {
            Id, StoreId, Name, LastName, SecondName, Birth, RegistrationDate, Gender, Email, Phone, Description
        }
    }
    @Serializable
    object EmailMessage : DataSource() {
        @Serializable
        enum class Field {
            Id, Email, Date, Time
        }
    }
    @Serializable
    object Campaign : DataSource() {
        @Serializable
        enum class Field {
            Id, Subject, UtmCampaign, UtmMedium, UtmSource
        }
    }

    @Serializable
    object None : DataSource()
}

@Serializable
data class EmailTemplateRowData(
    val id: String = "",
    val name: String = "",
    val row: EmailTemplateRow = EmailTemplateRow()
)

@Serializable
data class EmailTemplateRow(
    val options: EmailTemplateRowOptions = EmailTemplateRowOptions(),
    val columns: List<EmailTemplateColumn> = listOf(EmailTemplateColumn())
)

@Serializable
data class EmailTemplateColumn(
    val size: Int = 12, // 2-12 Total size must be 12
    val blocks: List<EmailTemplateBlock> = listOf(),
    val dataSource: DataSource = DataSource.None
)

@Serializable
data class EmailTemplateRowOptions(
    val backgroundColor: String = "transparent",
    val foregroundColor: String = "transparent",
    val verticalAlign: String = "top",
    val adaptToMobile: Boolean = true,
    val backOrderToMobileAdapt: Boolean = false,
    val backgroundImage: String? = null,
    val margins: List<Int> = listOf(0),
    val borders: List<BorderOptions> = listOf(BorderOptions(0, "transparent", ""))
)

@Serializable
data class BorderOptions(
    val size: Int = 0,
    val color: String = "transparent",
    val type: String = "solid"
)


interface EmailTemplateBlockProperties

interface HideProperties: EmailTemplateBlockProperties {
    val hide: Device?
}
interface ContentProperties : EmailTemplateBlockProperties {
    var content: String
}
interface FontSizeProperties {
    val fontSize: Int
}

interface FontProperties {
    val fontColor: String
    val lineHeight: Int
    val symbolsInterval: Int
}

interface FontTypeProperties {
    val font: String?
}

interface FontBoldProperties {
    val isBold: Boolean
}

interface RtlFontProperties {
    val rtlTextDirection: Boolean
}

interface LinkProperties {
    val linkColor: String
}

interface AlignProperties {
    val align: String
}

interface IntervalProperties {
    val interval: Int
}

interface MarginsProperties {
    val marginTop: Int
    val marginLeft: Int
    val marginRight: Int
    val marginBottom: Int
}

@Serializable
sealed class EmailTemplateBlock {
    @Serializable
    data class Header(
        val type: Int = 1,
        override var content: String = "Header",
        override val font: String? = null,
        override val isBold: Boolean = true,
        override val fontColor: String = "#000",
        override val linkColor: String = "#2200CC",
        override val align: String = "center",
        override val lineHeight: Int = 120,
        override val symbolsInterval: Int = 0,
        override val rtlTextDirection: Boolean = false,
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), ContentProperties, FontProperties, FontTypeProperties, FontBoldProperties, RtlFontProperties, LinkProperties, AlignProperties, MarginsProperties, HideProperties

    @Serializable
    data class Paragraph(
        override var content: String = "Paragraph",
        override val font: String? = null,
        override val isBold: Boolean = false,
        override val fontSize: Int = 14,
        override val fontColor: String = "#000",
        override val linkColor: String = "#2200CC",
        override val align: String = "center",
//        override val interval: Int = 16,
        override val lineHeight: Int = 120,
        override val symbolsInterval: Int = 0,
        override val rtlTextDirection: Boolean = false,
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), ContentProperties, FontProperties, FontTypeProperties, FontBoldProperties, FontSizeProperties, RtlFontProperties, LinkProperties, AlignProperties, MarginsProperties, HideProperties

    @Serializable
    data class List(
        override var content: String = "<li>Item 1</li><li>Item 2</li><li>Item 3</li>",
        val unorderedStyle: Boolean = true,
        override val font: String? = null,
        override val isBold: Boolean = false,
        override val fontSize: Int = 14,
        override val fontColor: String = "#000",
        override val linkColor: String = "#2200CC",
        override val align: String = "center",
        override val interval: Int = 0,
        val itemMargins: Int = 40,
        override val lineHeight: Int = 120,
        override val symbolsInterval: Int = 0,
        override val rtlTextDirection: Boolean = false,
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), ContentProperties, FontProperties, FontTypeProperties, FontBoldProperties, FontSizeProperties, RtlFontProperties, LinkProperties, AlignProperties, IntervalProperties, MarginsProperties, HideProperties

    @Serializable
    data class Image(
        val src: String = "",
        val alt: String = "",
        override val align: String = "center",
        val href: String = "",
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), AlignProperties, MarginsProperties, HideProperties

    @Serializable
    data class Video(
        val href: String = "",
        val src: String = "",
        val title: String = "",
        val icon: String = "arrow_light",
        val iconSize: Int = 50,
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), MarginsProperties, HideProperties

    @Serializable
    data class Button(
        var title: String = "Кнопка",
        val src: String = "",
        val width: Int? = null, // If null - AutoWidth, else width in percent (20%-100%)
        override val font: String? = null,
        override val isBold: Boolean = false,
        override val fontSize: Int = 14,
        override val fontColor: String = "#000",
        val backgroundColor: String = "#2200CC",
        override val align: String = "center",
        override val lineHeight: Int = 120,
        override val symbolsInterval: Int = 0,
        val cornersRadius: Int = 4,
        val paddings: kotlin.collections.List<Int> = List(4) { 0 },
        val borders: kotlin.collections.List<BorderOptions> = List(4) { BorderOptions(0, "transparent", "solid") },
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), FontProperties, FontTypeProperties, FontBoldProperties, FontSizeProperties, AlignProperties, MarginsProperties, HideProperties

    @Serializable
    data class Separator(
        val color: String = "#BBBBBB",
        val height: Int = 1,
        val type: String = "solid",
        val width: Int = 100,
        override val align: String = "center",
        override val hide: Device? = null,
    ): EmailTemplateBlock(), AlignProperties, HideProperties // solid, dotted, dashed

    @Serializable
    data class Spacer(val height: Int = 60, override val hide: Device? = null): EmailTemplateBlock(), HideProperties

    @Serializable
    data class Text(
        override var content: String = "Text",
        override val fontSize: Int = 14,
        override val fontColor: String = "#000",
        override val linkColor: String = "#2200CC",
        override val lineHeight: Int = 120,
        override val symbolsInterval: Int = 0,
        override val marginTop: Int = 0,
        override val marginLeft: Int = 0,
        override val marginRight: Int = 0,
        override val marginBottom: Int = 0,
        override val hide: Device? = null,
    ): EmailTemplateBlock(), ContentProperties, FontProperties, FontSizeProperties, LinkProperties, MarginsProperties, HideProperties

    @Serializable
    data class Html(
        override var content: String = "<div>Html content</div>",
        override val hide: Device? = null,
    ): EmailTemplateBlock(), ContentProperties, HideProperties
}

fun EmailTemplateBlock.copy(): EmailTemplateBlock {
    return when (this) {
        is EmailTemplateBlock.Button -> copy()
        is EmailTemplateBlock.Header -> copy()
        is EmailTemplateBlock.Html -> copy()
        is EmailTemplateBlock.Image -> copy()
        is EmailTemplateBlock.List -> copy()
        is EmailTemplateBlock.Paragraph -> copy()
        is EmailTemplateBlock.Separator -> copy()
        is EmailTemplateBlock.Spacer -> copy()
        is EmailTemplateBlock.Text -> copy()
        is EmailTemplateBlock.Video -> copy()
    }
}

@Serializable
data class GeneralEmailTemplateBlockSettings(
    val margin: Int? = 0,
    val margins: List<Int> = List(4) { 0 },
    val hide: Device? = null,
)

@Serializable
data class EmailTemplatePageSettings(
    val contentAreaWidth: Int = 600, // In px
    val contentAlign: String = "center",
    val backgroundColor: String = "#FFFFFF",
    val contentBackgroundColor: String = "transparent",
//    val image: Any
    val defaultFont: String = "",
    val defaultLinkColor: String = "#2200CC",
    val title: String = "",
    val subtitle: String = ""
)

@Serializable
enum class Device {
    Desktop,
    Mobile
}

//@Serializable
//enum class Align {
//    Left,
//    Center,
//    Right,
//    Justify
//}

fun EmailTemplateBlock.getBlockTextValue(): String {
    return when (this) {
        is EmailTemplateBlock.Header -> content
        is EmailTemplateBlock.Paragraph -> content
        is EmailTemplateBlock.List -> content
        is EmailTemplateBlock.Image -> ""
        is EmailTemplateBlock.Video -> ""
        is EmailTemplateBlock.Button -> title
        is EmailTemplateBlock.Separator -> ""
        is EmailTemplateBlock.Spacer -> ""
        is EmailTemplateBlock.Text -> content
        is EmailTemplateBlock.Html -> content
    }
}

private val regex = "\\{\\{[a-zA-Z0-9.]{1,256}\\}\\}".toRegex()
private val builder = StringBuilder()
fun String.renderDefaultBlockPreview(): String {
    if (!contains("{{")) return this

    val matches = regex.findAll(this).map { it.value }.toList()
    val parts = split(regex)

    builder.clear()
    builder.append(parts.first())

    for (i in matches.indices) {
        val variable = matches[i].substring(2, matches[i].length-2)

        val value = when (val variableGroup = variable.substringBefore(".")) {
            "data" -> {
                when (variable.substringAfterLast(".")) {
                    "id" -> "<ID>"
                    "name" -> "Наименование"
                    "available" -> "true"
                    "url" -> ""
                    "picture" -> "https://taras-rocket.ru/assets/img/logo.png"
                    "price" -> "100.0"
                    "old_price" -> ""
                    "discount" -> 0
                    "category" -> "<ID категории>"
                    "description" -> "Описание товара"
                    "vendor" -> "Производитель"
                    "vendor_code" -> "<Код производителя>"
                    else -> null
                }
            }
            "email" -> {
                when (variable.substringAfterLast(".")) {
                    "time" -> "Время"
                    "date" -> "Дата"
                    else -> matches[i]
                }
            }
            "campaign" -> {
                when (variable.substringAfterLast(".")) {
                    "id" -> "<ID кампании>"
                    "subject" -> "Тема письма"
                    "utm_campaign" -> "utm_campaign"
                    "utm_medium" -> "utm_medium"
                    "utm_source" -> "utm_source"
                    else -> null
                }
            }
            "store" -> {
                when (variable.substringAfterLast(".")) {
                    "name" -> "Название магазина"
                    "url" -> ""
                    else -> null
                }
            }
            "subscriber" ->
                when (variable.substringAfterLast(".")) {
                    "id" -> "<ID подписчика>"
                    "store_id" -> "<STORE_ID подписчика>"
                    "name" -> "<Имя подписчика>"
                    "registration_date" -> "<Дата регистрации>"
                    "email" -> "<Email подписчика>"
                    "phone" -> "<Телефон подписчика>"
                    "description" -> "<Описание подписчика>"
                    else -> null
                }
            else -> matches[i]
        }

        builder.append(value).append(parts[i+1])
    }

    return builder.toString()
}
fun EmailTemplateBlock.renderDefaultPreview(): String = getBlockTextValue().renderDefaultBlockPreview()


fun FontSizeProperties.copy(fontSize: Int): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Button -> block.copy(fontSize = fontSize)
        is EmailTemplateBlock.List -> block.copy(fontSize = fontSize)
        is EmailTemplateBlock.Paragraph -> block.copy(fontSize = fontSize)
        is EmailTemplateBlock.Text -> block.copy(fontSize = fontSize)
        else -> this
    }
}

fun FontProperties.copy(fontColor: String?, lineHeight: Int?, symbolsInterval: Int?): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Button -> block.copy(fontColor = fontColor ?: block.fontColor, lineHeight = lineHeight ?: block.lineHeight, symbolsInterval = symbolsInterval ?: block.symbolsInterval)
        is EmailTemplateBlock.Header -> block.copy(fontColor = fontColor ?: block.fontColor, lineHeight = lineHeight ?: block.lineHeight, symbolsInterval = symbolsInterval ?: block.symbolsInterval)
        is EmailTemplateBlock.List -> block.copy(fontColor = fontColor ?: block.fontColor, lineHeight = lineHeight ?: block.lineHeight, symbolsInterval = symbolsInterval ?: block.symbolsInterval)
        is EmailTemplateBlock.Paragraph -> block.copy(fontColor = fontColor ?: block.fontColor, lineHeight = lineHeight ?: block.lineHeight, symbolsInterval = symbolsInterval ?: block.symbolsInterval)
        is EmailTemplateBlock.Text -> block.copy(fontColor = fontColor ?: block.fontColor, lineHeight = lineHeight ?: block.lineHeight, symbolsInterval = symbolsInterval ?: block.symbolsInterval)
        else -> this
    }
}
fun FontTypeProperties.copy(font: String?): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Button -> block.copy(font = font)
        is EmailTemplateBlock.Header -> block.copy(font = font)
        is EmailTemplateBlock.List -> block.copy(font = font)
        is EmailTemplateBlock.Paragraph -> block.copy(font = font)
        else -> block
    }
}

fun FontBoldProperties.copy(isBold: Boolean): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Button -> block.copy(isBold = isBold)
        is EmailTemplateBlock.Header -> block.copy(isBold = isBold)
        is EmailTemplateBlock.List -> block.copy(isBold = isBold)
        is EmailTemplateBlock.Paragraph -> block.copy(isBold = isBold)
        else -> block
    }
}

fun RtlFontProperties.copy(rtlTextDirection: Boolean): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Header -> block.copy(rtlTextDirection = rtlTextDirection)
        is EmailTemplateBlock.List -> block.copy(rtlTextDirection = rtlTextDirection)
        is EmailTemplateBlock.Paragraph -> block.copy(rtlTextDirection = rtlTextDirection)
        else -> block
    }
}

fun LinkProperties.copy(linkColor: String): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Header -> block.copy(linkColor = linkColor)
        is EmailTemplateBlock.List -> block.copy(linkColor = linkColor)
        is EmailTemplateBlock.Paragraph -> block.copy(linkColor = linkColor)
        is EmailTemplateBlock.Text -> block.copy(linkColor = linkColor)
        else -> block
    }
}

fun AlignProperties.copy(align: String): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Button -> block.copy(align = align)
        is EmailTemplateBlock.Header -> block.copy(align = align)
        is EmailTemplateBlock.Image -> block.copy(align = align)
        is EmailTemplateBlock.List -> block.copy(align = align)
        is EmailTemplateBlock.Paragraph -> block.copy(align = align)
        is EmailTemplateBlock.Separator -> block.copy(align = align)
        else -> block
    }
}

fun IntervalProperties.copy(interval: Int): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.List -> block.copy(interval = interval)
//        is EmailTemplateBlock.Paragraph -> block.copy(interval = interval)
        else -> block
    }
}

fun MarginsProperties.copy(top: Int, left: Int, right: Int, bottom: Int): EmailTemplateBlock {
    return when (val block = this as EmailTemplateBlock) {
        is EmailTemplateBlock.Button -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        is EmailTemplateBlock.Header -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        is EmailTemplateBlock.Image -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        is EmailTemplateBlock.List -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        is EmailTemplateBlock.Paragraph -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        is EmailTemplateBlock.Text -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        is EmailTemplateBlock.Video -> block.copy(marginTop = top, marginLeft = left, marginRight = right, marginBottom = bottom)
        else -> block
    }
}

@Serializable
data class PreviewRowRequest(
    val row: EmailTemplateRow
)

@Serializable
enum class TemplateType(val urlTitle: String,val ruTitle: String){
    Campaign("campaign", "Шаблон рассылки"),
    EmailTemplate("email-template", "Шаблон письма"),
    Trigger("trigger","Шаблон Триггера"),
    Unsubscribe("unsubscribe","Шаблоны страниц отписки"),
    Confirm("confirm","Шаблоны подтверждения"),
    Review("review","Шаблоны страниц отзывов")
}


//@Serializable
//sealed class UnsubscribePageContent {
//
//    @Serializable
//    data class FreemarkerUnsubscribePageContent(
//        val template: EmailTemplate.Freemarker = EmailTemplate.Freemarker()
//    ): UnsubscribePageContent()
//
//    @Serializable
//    data class SimpleUnsubscribePageContent(
//        val header: String,
//        val footer: String
//    ): UnsubscribePageContent()
//
//}